import React, { forwardRef, useState, useEffect } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { toast } from "react-toastify";
import { Loader } from "lucide-react";
import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center cursor-pointer gap-2 w-fit justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-[#222325] text-white hover:bg-[#22232595]",
        destructive: "bg-red-600 text-white hover:bg-red-700",
        outline:
          "border border-secondary200 bg-white hover:bg-gray-50 text-gray-700",
        secondary: "bg-gray-100 text-gray-800 hover:bg-gray-200",
        ghost: "bg-transparent",
        link: "text-blue-600 underline-offset-4 hover:underline",
        white: "bg-[#ffffff] text-black hover:bg-neutral200",

      },
      size: {
        default: "md:px-4 md:py-3 p-2 rounded-lg",
        sm: "h-9 rounded-md px-3",
        lg: "h-12 rounded-md px-8",
        icon: "h-10 w-10",
        ghost:"p-0"
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const Button = forwardRef(
  (
    {
      className,
      variant,
      size,
      onClick,
      asChild = false,
      loading = false,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    const [isLoading, setLoading] = useState(loading);

    useEffect(() => {
      setLoading(loading);
    }, [loading]);

    const handleClick = (event) => {
      // Check if the user is offline
      if (!navigator.onLine) {
        toast.error("You are offline. Please check your internet connection.");
        setTimeout(() => {
          setLoading(false);
        }, 1000);
        return;
      }

      // Call the original onClick handler if provided
      if (onClick) {
        onClick(event);
      }
    };

    return (
      <>
        <Comp
          className={cn(
            "font-Inter",
            buttonVariants({ variant, size, className }),
            isLoading && "opacity-75 pointer-events-none"
          )}
          ref={ref}
          onClick={handleClick}
          disabled={isLoading || props.disabled}
          {...props}
        >
          {props.children}
          {isLoading && <Loader className="ml-2 animate-spin" />}
        </Comp>
      </>
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };
